import React, {
  Dispatch,
  FunctionComponent,
  ReactElement,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import styled, { css } from 'styled-components';
import { useMedia } from 'react-media-match';
import { FormattedMessage, useIntl } from 'react-intl';
import get from 'lodash.get';
import useConfig from '@empiriecom/module-components/hooks/useConfig';
import { Notification } from '@empiriecom/module-components/Notification';
import { formatCurrency } from '@empiriecom/module-components/FormattedCurrency';
import { FragmentConfiguration } from '@/config';
import { PaymentPause } from '../../InstallmentCalculatorGlobals/PaymentPause';
import { ViableInstallmentBox } from './ViableInstallmentBox';
import { InstallmentCalculatorFooter } from '../../InstallmentCalculatorGlobals/Footer';
import InstallmentPriceInput from '../../InstallmentCalculatorGlobals/PriceInput';
import OverviewTable from '../../InstallmentCalculatorGlobals/OverviewTable';
import { useInstallmentCalculatorContext } from '../../InstallmentCalculatorContext';

const ContentWrapper = styled.div`
  position: relative;
`;

const InstallmentCalculatorContainer = styled.div`
  margin: 1.312rem 0.687rem 0.687rem;
  color: #333;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
  background-color: rgba(255, 255, 255, 0.75);
  display: flex;
  justify-content: center;
  align-items: center;
`;

const InstallmentCalculatorWrapper = styled.div<{
  cardLayout: boolean;
  isMobile: boolean;
  borderColor: string;
}>`
  ${({ borderColor, cardLayout }) => !cardLayout && `border-bottom: .0625rem solid ${borderColor};`}
  ${({ isMobile }) => isMobile && 'flex-wrap: wrap;'}
  ${({ cardLayout }) => cardLayout && 'flex-wrap: wrap;'}
  display: flex;
  flex-direction: row;
  align-items: flex-start;
`;

const contentDesktopStyles = css`
  margin-right: 8%;
  width: 47%;
  min-width: 380px;
`;

const contentMobileStyles = css`
  width: 100%;
  margin-right: 0;
  min-width: 0;
`;

const InstallmentCalculatorContent = styled.div<{ cardLayout: boolean; isMobile: boolean }>`
  ${({ isMobile, cardLayout }) =>
    isMobile || cardLayout ? contentMobileStyles : contentDesktopStyles}
`;

const InstallmentPriceContainer = styled.div`
  background-color: #f5f5f5;
  text-align: center;
  padding: 1.313rem;
  font-size: 0.9375rem;
`;

const MonthlyInstallmentWrapper = styled.div`
  margin-top: 0.875rem;
  text-align: center;
`;

const MonthlyInstallmentPrice = styled.div`
  font-size: 2.625rem;
`;

const ViableInstallmentsDescription = styled.div`
  font-size: 0.938rem;
  line-height: 1.25rem;
  margin: 0.75rem 0 0.35rem;
`;

const ViableInstallmentsBoxes = styled.div<{ cardLayout: boolean }>`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-flow: row wrap;
  ${({ cardLayout }) => cardLayout && 'padding-bottom:1rem;'}
`;

export const DefaultInstallmentCalculator: FunctionComponent<{
  setCollapseThreshold: Dispatch<SetStateAction<number>>;
}> = ({ setCollapseThreshold }): ReactElement => {
  const [loadError, setLoadError] = useState<boolean>(false);
  const intl = useIntl();
  const mobile = useMedia({ desktop: false, mobile: true, tablet: false });
  const thresholdWrapperRef = useRef(null);
  const {
    installmentCalculator: { borderColor, cardLayout },
  } = useConfig<FragmentConfiguration>();
  const {
    error,
    isLoading,
    response,
    setNumberOfInstallments,
    setIsPaymentPause,
  } = useInstallmentCalculatorContext();
  useEffect(() => setCollapseThreshold(get(thresholdWrapperRef, 'current.clientHeight')));

  const errorMessage = (
    <FormattedMessage
      id="installment-calculator.error"
      defaultMessage="Error loading the installment calculator"
    />
  );

  return (
    <ContentWrapper>
      {error && setLoadError(true)}
      {loadError && (
        <Notification
          content={errorMessage}
          isInline
          level="error"
          onClose={() => setTimeout(() => setLoadError(false), 400)}
        />
      )}
      {isLoading && <LoadingOverlay />}
      {response && (
        <InstallmentCalculatorContainer data-testid="default-installment-calculator">
          <InstallmentCalculatorWrapper
            borderColor={borderColor}
            cardLayout={cardLayout}
            isMobile={mobile}
          >
            <InstallmentCalculatorContent
              cardLayout={cardLayout}
              isMobile={mobile}
              ref={thresholdWrapperRef}
            >
              <InstallmentPriceContainer>
                <InstallmentPriceInput />
                <MonthlyInstallmentWrapper>
                  <div>
                    <FormattedMessage
                      id="installment-calculator.monthly-rate"
                      defaultMessage="Monatliche Rate"
                    />
                  </div>
                  <MonthlyInstallmentPrice>
                    {formatCurrency(
                      response.currency,
                      response.singleInstallment.value * 100,
                      intl,
                    )}
                  </MonthlyInstallmentPrice>
                </MonthlyInstallmentWrapper>
              </InstallmentPriceContainer>
              <div>
                <ViableInstallmentsDescription>
                  <span>
                    <FormattedMessage
                      id="installment-calculator.viable-installments"
                      defaultMessage="Wähle deine gewünschten Raten"
                    />
                  </span>
                </ViableInstallmentsDescription>
                <ViableInstallmentsBoxes cardLayout={cardLayout}>
                  {response.viableInstallments.map(
                    (value: number): ReactElement => (
                      <ViableInstallmentBox
                        borderColor={borderColor}
                        isSelected={value === response.numberOfInstallments}
                        key={value}
                        onChange={() => setNumberOfInstallments(value)}
                        value={value}
                      />
                    ),
                  )}
                </ViableInstallmentsBoxes>
              </div>
              <PaymentPause
                onChange={() => setIsPaymentPause(!response.paymentPause)}
                paymentPause={response.paymentPause}
                borderColor={borderColor}
              />
            </InstallmentCalculatorContent>
            <OverviewTable borderColor={borderColor} response={response} />
          </InstallmentCalculatorWrapper>
          <InstallmentCalculatorFooter />
        </InstallmentCalculatorContainer>
      )}
    </ContentWrapper>
  );
};

export default DefaultInstallmentCalculator;
